import { useState } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import Modal from 'react-modal';
import PropTypes from 'prop-types';
import { IconButton, Button, Typography, TextField, MenuItem, Checkbox } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { PortalUsersRepository } from '../../repositories/portal_users_repository/portal_users_repository';

const userIsPendingCopy =
  "This user's account is in a pending state and needs to be approved or denied access by Roam before you can make further changes.";
const userIsBlockedCopy =
  "This user's account has been denied access by Roam. For this user to gain access ot the portal a new account will need top be created and approved before you can make further changes.";

export default function EditUserModal({ user, setShowEditUserModal }) {
  const [role, setRole] = useState(() => user.accountType);
  const [viewableSites, setViewableSites] = useState(() => user.viewableSites);
  const [isLoading, setIsLoading] = useState(false);

  const { portfolioFinancialData } = useSelector((state) => state.sites);
  const currentUser = useSelector((state) => state.user.user);

  const availableSites = portfolioFinancialData?.sites.map((site) => ({
    id: site.id,
    name: site.siteName,
  }));

  const notApprovedUser = ['pending', 'denied'].includes(user.accountStatus);

  const modalText = (() => {
    if (user.accountStatus === 'pending') {
      return userIsPendingCopy;
    }
    if (user.accountStatus === 'denied') {
      return userIsBlockedCopy;
    }
  })();

  const modalTitle = (() => (notApprovedUser ? 'Unable to edit user' : 'Edit user details'))();

  const handleRequestUpdateUser = async () => {
    setIsLoading(true);

    const payload = {
      userId: user.id,
      email: user.email,
      requestedBy: currentUser.email,
      role,
      selectedSites: viewableSites,
    };

    try {
      await PortalUsersRepository.requestUpdateUser(payload, currentUser.token);
      setIsLoading(false);
      toast.success('The request to Update this user has been sent');
      setShowEditUserModal(false);
    } catch (error) {
      setIsLoading(false);
      toast.error('Something went wrong. Please try again or contact support');
      setShowEditUserModal(false);
    }
  };

  return (
    <Modal
      shouldCloseOnOverlayClick={false}
      isOpen
      onRequestClose={() => setShowEditUserModal(false)}
      style={{
        overlay: {
          backgroundColor: 'rgba(0, 0, 0, 0.3)',
          zIndex: 1000,
        },
        content: {
          position: 'relative',
          paddingBottom: '0px',
          width: '472px',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          padding: '10px',
          borderRadius: '16px',
          boxShadow: '0px 2px 5px rgba(0, 0, 0, 0.3)',
        },
      }}
    >
      <div className="p-[24px]">
        <div className="flex justify-between mb-[24px]">
          <h1 className="text-[24px]">{modalTitle}</h1>
          <IconButton
            disabled={isLoading}
            onClick={() => setShowEditUserModal(false)}
            size="small"
            aria-label="edit"
          >
            <CloseIcon color="success" />
          </IconButton>
        </div>

        {notApprovedUser && <p className="text-[14px] mb-[24px]">{modalText}</p>}
        {!notApprovedUser && (
          <>
            <TextField
              fullWidth
              label="Email"
              color="success"
              defaultValue={user.email}
              InputProps={{
                readOnly: true,
              }}
              sx={{ marginBottom: '16px' }}
            />

            <TextField
              value={role}
              onChange={(e) => setRole(e.target.value)}
              label="Role"
              select
              fullWidth
              color="success"
              sx={{ marginBottom: '16px' }}
            >
              <MenuItem key={'user'} value={'user'}>
                Site Manager
              </MenuItem>
              <MenuItem key={'admin'} value={'admin'}>
                Portfolio Manager
              </MenuItem>
            </TextField>

            {role === 'user' && (
              <TextField
                label="Viewable Sites"
                select
                fullWidth
                color="success"
                sx={{ marginBottom: '16px' }}
                SelectProps={{
                  multiple: true,
                  value: viewableSites,
                  onChange: (e) => setViewableSites(e.target.value),
                  renderValue: (selected) =>
                    selected
                      .map((siteId) => {
                        const site = availableSites.find((s) => s.id === siteId);
                        return site ? site.name : '';
                      })
                      .join(', '),
                }}
              >
                {availableSites.map((site) => (
                  <MenuItem key={site.id} value={site.id}>
                    <Checkbox color="success" checked={viewableSites.indexOf(site.id) > -1} />
                    {site.name}
                  </MenuItem>
                ))}
              </TextField>
            )}
          </>
        )}

        <div className="flex justify-end">
          <Button
            disabled={isLoading}
            sx={{ borderRadius: 8, height: '40px', paddingX: '24px' }}
            variant="text"
            color="success"
            onClick={() => setShowEditUserModal(false)}
          >
            <Typography fontSize={14} textTransform={'none'}>
              Cancel
            </Typography>
          </Button>
          {!notApprovedUser && (
            <Button
              onClick={handleRequestUpdateUser}
              disabled={isLoading}
              sx={{
                borderRadius: 8,
                height: '40px',
              }}
              variant="contained"
              color="success"
            >
              <Typography fontSize={14} textTransform={'none'}>
                {isLoading ? 'Requesting...' : 'Request Update'}
              </Typography>
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
}

EditUserModal.propTypes = {
  user: PropTypes.shape({
    id: PropTypes.string.isRequired,
    email: PropTypes.string.isRequired,
    firstName: PropTypes.string.isRequired,
    lastName: PropTypes.string.isRequired,
    accountType: PropTypes.oneOf(['user', 'admin']).isRequired,
    viewableSites: PropTypes.arrayOf(PropTypes.string).isRequired,
    accountStatus: PropTypes.oneOf(['approved', 'pending', 'denied']).isRequired,
  }).isRequired,
  setShowEditUserModal: PropTypes.func.isRequired,
};
